import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { ProtectedRoute } from "@/core/auth/guards/ProtectedRoute";
import { navigateToProviderRedirect } from "@/core/routing/externalNavigation";
import { Button } from "@/shared/components/shadcn/Button";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { OAuthScopes } from "../components/OAuthScopes";
import { useOAuthAuthorization } from "../hooks/oauthQueries";
import { useOAuthOperation } from "../hooks/useOAuthOperation";
import { authorizationIdFromSearch, isSupportedMcpClient, mcpEnabled, oauthService, supportsOAuthManagement } from "../services/oauthService";

function Authorization({ authorizationId, userId }: { authorizationId: string; userId: string }) {
  const query = useOAuthAuthorization(userId, authorizationId);
  const beginOperation = useOAuthOperation();
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const result = query.data;
  async function decide(approve: boolean) {
    if (submitting.current || result?.kind !== "consent") return;
    const isCurrent = beginOperation();
    submitting.current = true;
    setBusy(true);
    setError(undefined);
    try {
      const redirectUrl = await oauthService.decide(authorizationId, result.details, approve);
      if (isCurrent()) navigateToProviderRedirect(redirectUrl);
    } catch {
      if (isCurrent()) setError("Couldn't complete authorization. The request may have expired. Please try again or restart from your app.");
    } finally {
      submitting.current = false;
      if (isCurrent()) setBusy(false);
    }
  }
  if (query.isPending) return <LoadingSpinner message="Loading authorization request..." />;
  if (query.error || !result) return <ErrorState title="Authorization unavailable" message="This request may be invalid or expired. Retry or start a new connection from your app." onRetry={() => void query.refetch()} />;
  if (result.kind === "redirect") return <Section title="Authorization already completed" card>
    <p className="text-sm text-muted-foreground">Froppio's authorization provider returned a destination for this request. Continue back to the requesting app.</p>
    <Button onClick={() => {
      try { navigateToProviderRedirect(result.redirectUrl); }
      catch { setError("Couldn't return to the app. Restart authorization in your browser."); }
    }}>Continue to app</Button>
    {error && <ErrorState message={error} />}
  </Section>;
  const { client, scope, user } = result.details;
  const supported = isSupportedMcpClient(client.id);
  return <div className="space-y-6" aria-busy={busy}>
    <Section title={client.name} card>
      <p className="break-all text-sm text-muted-foreground">Client ID: {client.id}</p>
      <p className="break-all text-sm">Signed in as {user.email}</p>
      {supported ? <>
        <p className="font-medium">Read-only access to Froppio</p>
        <p className="text-sm text-muted-foreground">This supported MCP app can read businesses you own, appointments and availability, and search your clients. It cannot create, change or delete your Froppio data.</p>
        <p className="text-sm text-muted-foreground">Finding available slots also reads business location names and active service catalogue details: names, durations and scheduling buffers.</p>
        <p className="text-sm text-muted-foreground">Client search shares names, email addresses and phone numbers. Appointment lookup shares appointment times, status and service durations.</p>
        <p className="text-sm text-muted-foreground">Access applies to your account's owned businesses, not just your currently selected business. Only connect apps you trust with customer information.</p>
      </> : <ErrorState title="Unsupported app" message="This client is not approved for Froppio's read-only MCP integration. Do not continue. You can deny this request." />}
    </Section>
    <Section title="Requested identity scopes" card>
      <OAuthScopes scopes={scope.split(/\s+/)} />
      <p className="text-sm text-muted-foreground">Identity scopes describe account information. Froppio's MCP server separately restricts business-data access.</p>
    </Section>
    {error && <ErrorState message={error} />}
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" disabled={busy} onClick={() => void decide(false)}>Deny</Button>
      {supported && <Button disabled={busy} onClick={() => void decide(true)}>{busy ? "Please wait…" : "Allow read-only access"}</Button>}
    </div>
    <p className="text-sm text-muted-foreground">Manage or disconnect this grant later under More → Connected apps. Disconnecting blocks new Froppio MCP requests; in-flight requests may finish. Information already shared with the app is not deleted.</p>
  </div>;
}

function AuthenticatedAuthorization({ authorizationId }: { authorizationId: string }) {
  const { user } = useAuth();
  return user ? <Authorization key={`${user.id}:${authorizationId}`} userId={user.id} authorizationId={authorizationId} /> : null;
}

export function OAuthConsentPage() {
  const location = useLocation();
  const authorizationId = authorizationIdFromSearch(location.search);
  return <Page>
    <PageHeader title="Connect an app" />
    <PageContent className="max-w-xl space-y-6">
      {!supportsOAuthManagement() ? <ErrorState title="Use your browser" message="For your security, connect apps in the standalone Froppio browser app, not inside an embedded or native app." /> :
        !mcpEnabled ? <ErrorState title="Connections unavailable" message="Froppio MCP connections are not enabled on this deployment." /> :
        !authorizationId ? <ErrorState title="Invalid authorization request" message="Start a new connection from your supported MCP app." /> :
        <ProtectedRoute returnTo={`/oauth/consent?${new URLSearchParams({ authorization_id: authorizationId })}`}>
          <AuthenticatedAuthorization authorizationId={authorizationId} />
        </ProtectedRoute>}
    </PageContent>
  </Page>;
}
